/**
 * StatusBadge — renders a `Message.status` value as both an `<svg>` icon and
 * a visible text label.
 *
 * Contract (Property 30 / Requirement 10.7): for every `MessageStatus` value,
 * the rendered output contains BOTH an `<svg>` node and a non-empty visible
 * text label, so state is never conveyed by color alone. The icon carries
 * `aria-hidden="true"` because the localized text is the accessible name; the
 * surrounding `<span>` exposes a `role="status"` plus an `aria-label` so
 * assistive tech announces the badge consistently regardless of layout.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { MessageStatus } from '../../domain/types';

import styles from './StatusBadge.module.css';

interface StatusBadgeProps {
  status: MessageStatus;
  /** Optional extra class name. */
  className?: string | undefined;
}

interface IconProps {
  /** Aligns with parent text size; overridable per badge. */
  className?: string | undefined;
}

/* ------------------------------------------------------------------------- */
/* Inline SVG icons                                                          */
/*                                                                           */
/* All icons are 16x16, stroke-based, and use `currentColor` so they pick up */
/* the badge's text color. They are stateless and `aria-hidden` because the  */
/* visible/aria-label text already names the status.                         */
/* ------------------------------------------------------------------------- */

function ClockIcon({ className }: IconProps): ReactElement {
  return (
    <svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
      <path
        d="M8 4v4l2.5 2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

function CheckIcon({ className }: IconProps): ReactElement {
  return (
    <svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <path
        d="M3 8.5l3 3 7-7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

function DotsIcon({ className }: IconProps): ReactElement {
  return (
    <svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <circle cx="3.5" cy="8" r="1.5" fill="currentColor" />
      <circle cx="8" cy="8" r="1.5" fill="currentColor" />
      <circle cx="12.5" cy="8" r="1.5" fill="currentColor" />
    </svg>
  );
}

function CheckCircleIcon({ className }: IconProps): ReactElement {
  return (
    <svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" fill="none" />
      <path
        d="M5 8.25l2 2 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

function XIcon({ className }: IconProps): ReactElement {
  return (
    <svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <path
        d="M4 4l8 8M12 4l-8 8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}

function WarningIcon({ className }: IconProps): ReactElement {
  return (
    <svg
      viewBox="0 0 16 16"
      width="16"
      height="16"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      <path
        d="M8 1.5l7 12.5H1L8 1.5z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
        fill="none"
      />
      <path d="M8 6.5v3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <circle cx="8" cy="12" r="0.75" fill="currentColor" />
    </svg>
  );
}

/**
 * Map every `MessageStatus` value to the icon component used inside the badge.
 *
 * The map is keyed by the full `MessageStatus` union, so adding a new status
 * later will surface as a TypeScript error here — that is the intended
 * compile-time guard for Property 30.
 */
const STATUS_ICONS: Record<MessageStatus, (props: IconProps) => ReactElement> = {
  queued: ClockIcon,
  sent: CheckIcon,
  streaming: DotsIcon,
  complete: CheckCircleIcon,
  failed: XIcon,
  softFallback: WarningIcon,
};

/**
 * Map every `MessageStatus` value to the variant class name. Kept separate
 * from the icon map so theming (color tokens) and iconography evolve
 * independently.
 */
const STATUS_VARIANT_CLASS: Record<MessageStatus, string> = {
  queued: styles.queued ?? '',
  sent: styles.sent ?? '',
  streaming: styles.streaming ?? '',
  complete: styles.complete ?? '',
  failed: styles.failed ?? '',
  softFallback: styles.softFallback ?? '',
};

export function StatusBadge({ status, className }: StatusBadgeProps): ReactElement {
  const { t } = useTranslation();
  const label = t(`status.${status}`);
  const Icon = STATUS_ICONS[status];
  const variantClass = STATUS_VARIANT_CLASS[status] ?? '';

  const classes = [styles.badge, variantClass, className].filter(Boolean).join(' ');

  return (
    <span
      className={classes}
      role="status"
      aria-label={label}
      data-status={status}
      data-testid="status-badge"
    >
      {Icon ? <Icon className={styles.icon ?? ''} /> : null}
      <span className={styles.label}>{label}</span>
    </span>
  );
}
